import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Localization, SessionContext } from "@viamap/viamap2-common";
import { ADModalBody, ADModalFooter, AdvancedDragModal } from "src/componentsUtils/AdvancedDragModal";
import { GlassButton, GlassButtonRow } from "src/components/GlassButtons";
import { ApplicationStateContext, Feature } from "src/states/ApplicationState";
import { migratePLAExpression, PLAExpressionExplanation, simulatePLAExpression, validatePLAExpression } from "./PLAExpression";
import { PLAExpressionAssistResult, requestPLAExpressionSuggestion } from "./PLAExpressionAssistant";
import { PLAExpressionHelpDialog } from "./PLAExpressionHelpDialog";
import { PLAExpressionInput } from "./PLAExpressionInput";
import { PLAElementRecord, PLAProject, PLARelationRecord } from "./PLAState";

export type ExpressionExplanationResult = PLAExpressionExplanation | { expression: string; error: string; warningMessages?: string[] };

export function ExpressionExplanationView(props: { explanation: ExpressionExplanationResult; showResult?: boolean }) {
   const explanation = props.explanation;
   return <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      <strong>{explanation.expression}</strong>
      {"error" in explanation ? (
         <pre role="alert" style={{ whiteSpace: "pre-wrap", margin: 0 }}>{[explanation.error, ...(explanation.warningMessages?.length ? ["", `${Localization.getText("Warning")}:`, ...explanation.warningMessages.map((message) => `  ${message}`)] : [])].join("\n")}</pre>
      ) : (
         <>
            {props.showResult && <pre role="status" style={{ whiteSpace: "pre-wrap", margin: 0 }}>{explanation.lines[0]}</pre>}
            <details>
               <summary style={{ cursor: "pointer" }}>{Localization.getText("Details")}</summary>
               <pre style={{ whiteSpace: "pre-wrap", margin: "8px 0 0" }}>{explanation.lines.join("\n")}</pre>
            </details>
         </>
      )}
   </div>;
}

type Props = {
   project: PLAProject;
   elementTypeId: string;
   portalRoot: Element;
   onClose: () => void;
   /** Sample element the expression is evaluated from. Without it only syntax checking and completion are available. */
   element?: PLAElementRecord;
   elements?: PLAElementRecord[];
   relations?: PLARelationRecord[];
   initialExpression?: string;
   /** When provided the dialog works as an expression editor and returns a valid expression through this callback. */
   onApply?: (expression: string) => void;
};

function PLAExpressionAIPanel(props: { project: PLAProject; elementTypeId: string; elements: PLAElementRecord[]; relations: PLARelationRecord[]; currentExpression: string; onUse: (expression: string) => void }) {
   const { state: sessionState } = useContext(SessionContext);
   const [description, setDescription] = useState("");
   const [busy, setBusy] = useState(false);
   const [proposal, setProposal] = useState<PLAExpressionAssistResult>();
   const [error, setError] = useState<string>();
   const abort = useRef<AbortController>();
   useEffect(() => () => abort.current?.abort(), []);

   const generate = async () => {
      abort.current?.abort();
      const controller = new AbortController();
      abort.current = controller;
      setBusy(true);
      setError(undefined);
      setProposal(undefined);
      try {
         setProposal(await requestPLAExpressionSuggestion({
            description,
            language: Localization.getLanguage(),
            project: props.project,
            elementTypeId: props.elementTypeId,
            elements: props.elements,
            relations: props.relations,
            currentExpression: props.currentExpression,
            caller: { userRef: sessionState.userRef, userName: sessionState.userName, organizationId: sessionState.customerRef },
            signal: controller.signal,
         }));
      } catch (caught) {
         if (!controller.signal.aborted) setError(caught instanceof Error ? caught.message : String(caught));
      } finally {
         if (abort.current === controller) setBusy(false);
      }
   };

   return <details style={{ marginTop: 12 }}>
      <summary style={{ cursor: "pointer" }}>{Localization.getText("Expression AI assistant")}</summary>
      <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 8 }}>
         <p style={{ margin: 0 }}>{Localization.getText("Expression AI assistant hint")}</p>
         <textarea value={description} onChange={(event) => setDescription(event.target.value)} rows={3} maxLength={4000} style={{ resize: "vertical", width: "100%" }} placeholder={Localization.getText("Expression AI assistant placeholder")}
            onKeyDown={(event) => { if (event.key === "Enter" && (event.ctrlKey || event.metaKey) && description.trim() && !busy) void generate(); }} />
         <GlassButtonRow>
            <GlassButton disabled={busy || !description.trim()} onClick={() => void generate()}>{Localization.getText(busy ? "Expression AI generating" : "Expression AI generate")}</GlassButton>
         </GlassButtonRow>
         {error && <pre role="alert" style={{ whiteSpace: "pre-wrap", margin: 0 }}>{error}</pre>}
         {proposal && <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {proposal.expression ? <code style={{ whiteSpace: "pre-wrap" }}>{proposal.expression}</code> : <em>{Localization.getText("Expression AI no proposal")}</em>}
            {proposal.explanation && <span>{proposal.explanation}</span>}
            {proposal.expression && !proposal.valid && <span role="alert">{Localization.getText("Expression AI invalid proposal")}: {proposal.error}</span>}
            {proposal.expression && <GlassButtonRow>
               <GlassButton onClick={() => props.onUse(proposal.expression)}>{Localization.getText("Expression AI use proposal")}</GlassButton>
            </GlassButtonRow>}
         </div>}
      </div>
   </details>;
}

export function PLAExpressionSimulationDialog(props: Props) {
   const { hasAccessToFeature } = useContext(ApplicationStateContext);
   const [expression, setExpression] = useState(props.initialExpression || "");
   const [result, setResult] = useState<ExpressionExplanationResult>();
   const [showHelp, setShowHelp] = useState(false);
   const elements = props.elements || [];
   useEffect(() => setResult(undefined), [expression, props.element, props.project, props.elements, props.relations]);

   const valid = validatePLAExpression(expression, { project: props.project, elementTypeId: props.elementTypeId }).valid && Boolean(expression.trim());
   const migrated = useMemo(() => {
      try {
         const next = migratePLAExpression(expression);
         return next !== expression ? next : undefined;
      } catch {
         return undefined;
      }
   }, [expression]);
   const evaluate = () => {
      if (!props.element) return;
      try {
         setResult(simulatePLAExpression(expression, { element: props.element, elements, relations: props.relations || [], project: props.project }));
      } catch (error) {
         setResult({ expression, error: expression.trim() ? error instanceof Error ? error.message : String(error) : Localization.getText("Expression is required") });
      }
   };

   return createPortal(
      <AdvancedDragModal title={Localization.getText(props.onApply ? "Expression editor" : "Expression simulation")} PosDefault={{ width: "720px", left: "50%", transform: "translateX(-50%)", top: "120px" }} onClose={props.onClose}>
         <ADModalBody>
            <p>{Localization.getText(props.element ? "Expression simulation hint" : "Expression editor hint")}</p>
            {props.element && <p><strong>{Localization.getText("Element")}: {props.element.name || Localization.getText("Unnamed element")}</strong></p>}
            <PLAExpressionInput value={expression} onChange={setExpression} project={props.project} elementTypeId={props.elementTypeId} elements={elements} />
            {hasAccessToFeature(Feature.PLAToolGlobalAdmin) && <PLAExpressionAIPanel project={props.project} elementTypeId={props.elementTypeId} elements={elements} relations={props.relations || []} currentExpression={expression} onUse={setExpression} />}
            {result && <div style={{ marginTop: 12 }}><ExpressionExplanationView explanation={result} showResult /></div>}
         </ADModalBody>
         <ADModalFooter>
            <GlassButtonRow>
               <GlassButton onClick={() => setShowHelp(true)}>{Localization.getText("Expression syntax help")}</GlassButton>
               {migrated && <GlassButton onClick={() => setExpression(migrated)}>{Localization.getText("Migrate expression")}</GlassButton>}
               {props.element && <GlassButton disabled={!expression.trim()} onClick={evaluate}>{Localization.getText("Evaluate expression")}</GlassButton>}
               {props.onApply && <GlassButton disabled={!valid} onClick={() => props.onApply?.(expression)}>{Localization.getText("Use expression")}</GlassButton>}
               <GlassButton onClick={props.onClose}>{Localization.getText("Close")}</GlassButton>
            </GlassButtonRow>
         </ADModalFooter>
         {showHelp && <PLAExpressionHelpDialog onClose={() => setShowHelp(false)} />}
      </AdvancedDragModal>,
      props.portalRoot
   );
}
